// src/components/admin/BlogAnalyticsDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiBarChart2,
  FiPieChart,
  FiTrendingUp,
  FiCalendar,
  FiMessageCircle,
  FiEye,
  FiThumbsUp,
  FiStar,
  FiClock,
  FiUsers,
  FiActivity,
  FiAward,
  FiTag,
  FiFolder,
  FiDownload,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiHeart,
  FiSmile,
  FiFrown,
  FiThumbsDown,
  FiTrendingDown,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  ComposedChart,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, isDark, unit = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="p-3 rounded-lg shadow-lg border"
        style={{
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
        >
          {label}
        </p>
        {payload.map((item, index) => (
          <p
            key={index}
            className="text-xs mt-1"
            style={{ color: isDark ? '#94a3b8' : '#64748b' }}
          >
            {item.name}:{' '}
            <span className="font-bold" style={{ color: item.color }}>
              {item.value} {unit}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, color, subtitle }) => {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group"
      style={{
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
      }}
    >
      <div className="absolute top-0 right-0 w-24 h-24 opacity-5 group-hover:opacity-10 transition-opacity">
        <Icon className="w-full h-full" />
      </div>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--muted)' }}
          >
            {title}
          </span>
          <div
            className="p-2 rounded-xl"
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
        </div>
        <div
          className="text-3xl font-bold mb-1"
          style={{ color: 'var(--text)' }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 text-xs">
            {trend >= 0 ? (
              <FiTrendingUp className="w-3 h-3 text-green-500" />
            ) : (
              <FiTrendingDown className="w-3 h-3 text-red-500" />
            )}
            <span className={trend >= 0 ? 'text-green-500' : 'text-red-500'}>
              {Math.abs(trend)}%
            </span>
            <span style={{ color: 'var(--muted)' }}>vs last period</span>
          </div>
        )}
        {subtitle && (
          <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

// Main Blog Analytics Component
const BlogAnalyticsDashboard = ({ blogId = null }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { getAuthToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30days');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Chart colors
  const chartColors = useMemo(
    () => ({
      primary: '#8b5cf6',
      primaryLight: '#a78bfa',
      secondary: '#ec4899',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      cyan: '#06b6d4',
      purple: '#a855f7',
      orange: '#f97316',
      indigo: '#6366f1',
      text: isDark ? '#f1f5f9' : '#1e293b',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? '#334155' : '#e2e8f0',
    }),
    [isDark],
  );

  const PIE_COLORS = [
    chartColors.primary,
    chartColors.secondary,
    chartColors.warning,
    chartColors.success,
    chartColors.cyan,
    chartColors.purple,
    chartColors.info,
    chartColors.orange,
    chartColors.indigo,
  ];

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      if (!token) return;

      // Fetch overview analytics
      const overviewResponse = await fetch(
        `/api/blogs?action=analytics-overview&period=${period}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const overviewData = await overviewResponse.json();

      if (overviewData.success) {
        setAnalyticsData(overviewData);
      }

      // If blogId is provided, fetch blog detail analytics
      if (blogId) {
        const detailResponse = await fetch(
          `/api/blogs?action=analytics-blog-detail&blogId=${blogId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const detailData = await detailResponse.json();
        if (detailData.success) {
          // Merge detail data with overview
          setAnalyticsData(prev => ({
            ...prev,
            blogDetail: detailData,
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthToken, period, blogId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: chartColors.primary }}
        />
      </div>
    );
  }

  const overview = analyticsData?.overview || {};
  const distribution = analyticsData?.distribution || {};
  const performance = analyticsData?.performance || {};
  const engagement = analyticsData?.engagement || {};
  const blogDetail = analyticsData?.blogDetail || null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            {blogId ? 'Blog Analytics' : 'Blog Management Analytics'}
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            {blogId
              ? `Detailed analytics for ${blogDetail?.blog?.title || 'this blog'}`
              : 'Comprehensive insights into your blog performance'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm outline-none cursor-pointer"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="365days">Last Year</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Blog Detail Header */}
      {blogId && blogDetail && (
        <div
          className="p-5 rounded-2xl border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <h3
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {blogDetail.blog.title}
              </h3>
              <div
                className="flex flex-wrap items-center gap-3 mt-2 text-sm"
                style={{ color: 'var(--muted)' }}
              >
                <span className="flex items-center gap-1">
                  <FiFolder className="w-3 h-3" />
                  {blogDetail.blog.category}
                </span>
                <span className="flex items-center gap-1">
                  <FiEye className="w-3 h-3" />
                  {blogDetail.blog.views} views
                </span>
                <span className="flex items-center gap-1">
                  <FiThumbsUp className="w-3 h-3" />
                  {blogDetail.blog.likes} likes
                </span>
                <span className="flex items-center gap-1">
                  <FiClock className="w-3 h-3" />
                  {blogDetail.blog.readingTime} min read
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    blogDetail.blog.status === 'published'
                      ? 'bg-green-500/20 text-green-500'
                      : 'bg-yellow-500/20 text-yellow-500'
                  }`}
                >
                  {blogDetail.blog.status}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                Engagement Rate
              </div>
              <div
                className="text-2xl font-bold"
                style={{ color: chartColors.success }}
              >
                {blogDetail.performance.engagementRate}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Blogs"
          value={blogId ? 1 : overview.totalBlogs || 0}
          icon={FiFolder}
          color={chartColors.primary}
        />
        <StatCard
          title="Total Views"
          value={
            blogId ? blogDetail?.blog?.views || 0 : overview.totalViews || 0
          }
          icon={FiEye}
          color={chartColors.info}
        />
        <StatCard
          title="Total Comments"
          value={
            blogId
              ? blogDetail?.comments?.total || 0
              : overview.totalComments || 0
          }
          icon={FiMessageCircle}
          color={chartColors.warning}
        />
        <StatCard
          title="Total Reactions"
          value={
            blogId
              ? blogDetail?.comments?.totalReactions || 0
              : overview.totalReactions || 0
          }
          icon={FiHeart}
          color={chartColors.secondary}
        />
        <StatCard
          title="Engagement Rate"
          value={
            blogId
              ? `${blogDetail?.performance?.engagementRate || 0}%`
              : `${overview.engagementRate || 0}%`
          }
          icon={FiActivity}
          color={chartColors.success}
        />
        <StatCard
          title="Avg Reading Time"
          value={
            blogId
              ? `${blogDetail?.blog?.readingTime || 0} min`
              : `${overview.avgReadingTime || 0} min`
          }
          icon={FiClock}
          color={chartColors.cyan}
        />
      </div>

      {/* Category & Status Distribution */}
      {!blogId && distribution.categories?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Distribution */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: chartColors.text }}
            >
              <FiTag
                className="w-4 h-4"
                style={{ color: chartColors.primary }}
              />
              Posts by Category
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distribution.categories}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    {distribution.categories.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend
                    formatter={value => (
                      <span
                        style={{
                          color: chartColors.textSecondary,
                          fontSize: '11px',
                        }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: chartColors.text }}
            >
              <FiPieChart
                className="w-4 h-4"
                style={{ color: chartColors.secondary }}
              />
              Posts by Status
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: 'Published',
                        value: distribution.status?.published || 0,
                        color: chartColors.success,
                      },
                      {
                        name: 'Draft',
                        value: distribution.status?.draft || 0,
                        color: chartColors.warning,
                      },
                      {
                        name: 'Scheduled',
                        value: distribution.status?.scheduled || 0,
                        color: chartColors.cyan,
                      },
                      {
                        name: 'Archived',
                        value: distribution.status?.archived || 0,
                        color: chartColors.error,
                      },
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                  >
                    {[
                      chartColors.success,
                      chartColors.warning,
                      chartColors.cyan,
                      chartColors.error,
                    ].map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend
                    formatter={value => (
                      <span
                        style={{
                          color: chartColors.textSecondary,
                          fontSize: '11px',
                        }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Comments & Reactions Over Time */}
      {engagement.commentsOverTime?.length > 0 && (
        <div
          className="p-5 rounded-2xl border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          <h3
            className="font-semibold flex items-center gap-2 mb-4"
            style={{ color: chartColors.text }}
          >
            <FiCalendar
              className="w-4 h-4"
              style={{ color: chartColors.cyan }}
            />
            Comments & Engagement Over Time
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={engagement.commentsOverTime}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartColors.grid}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                  interval={
                    period === '30days' ? 5 : period === '90days' ? 14 : 30
                  }
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Legend wrapperStyle={{ color: chartColors.textSecondary }} />
                <Bar
                  yAxisId="left"
                  dataKey="count"
                  name="Comments"
                  fill={chartColors.warning}
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="views"
                  name="Views"
                  stroke={chartColors.info}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="likes"
                  name="Likes"
                  stroke={chartColors.secondary}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Reaction Distribution */}
      {engagement.reactionDistribution &&
        Object.values(engagement.reactionDistribution).some(v => v > 0) && (
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: chartColors.text }}
            >
              <FiHeart
                className="w-4 h-4"
                style={{ color: chartColors.secondary }}
              />
              Reaction Distribution
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={Object.entries(engagement.reactionDistribution)
                        .map(([key, value]) => ({
                          name: key.charAt(0).toUpperCase() + key.slice(1),
                          value,
                          emoji:
                            key === 'like'
                              ? '👍'
                              : key === 'love'
                                ? '❤️'
                                : key === 'haha'
                                  ? '😂'
                                  : key === 'sad'
                                    ? '😢'
                                    : '😠',
                        }))
                        .filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                      labelLine={false}
                    >
                      {Object.keys(engagement.reactionDistribution)
                        .filter(k => engagement.reactionDistribution[k] > 0)
                        .map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDark ? '#1f2937' : '#ffffff',
                        borderColor: isDark ? '#374151' : '#e5e7eb',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {Object.entries(engagement.reactionDistribution).map(
                  ([key, value]) => {
                    if (value === 0) return null;
                    const total = Object.values(
                      engagement.reactionDistribution,
                    ).reduce((a, b) => a + b, 0);
                    const percentage =
                      total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    const emoji =
                      key === 'like'
                        ? '👍'
                        : key === 'love'
                          ? '❤️'
                          : key === 'haha'
                            ? '😂'
                            : key === 'sad'
                              ? '😢'
                              : '😠';
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <div className="w-8 text-xl">{emoji}</div>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span style={{ color: chartColors.text }}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </span>
                            <span style={{ color: chartColors.textSecondary }}>
                              {value.toLocaleString()}
                            </span>
                          </div>
                          <div
                            className="h-2 rounded-full overflow-hidden"
                            style={{ backgroundColor: chartColors.grid }}
                          >
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor:
                                  PIE_COLORS[
                                    Object.keys(
                                      engagement.reactionDistribution,
                                    ).indexOf(key) % PIE_COLORS.length
                                  ],
                              }}
                            />
                          </div>
                        </div>
                        <span
                          className="text-sm"
                          style={{ color: chartColors.textSecondary }}
                        >
                          {percentage}%
                        </span>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        )}

      {/* Top Performing Blogs */}
      {performance.topPerforming?.length > 0 && !blogId && (
        <div
          className="p-5 rounded-2xl border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          <h3
            className="font-semibold flex items-center gap-2 mb-4"
            style={{ color: chartColors.text }}
          >
            <FiAward
              className="w-4 h-4"
              style={{ color: chartColors.warning }}
            />
            Top Performing Blogs
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${chartColors.grid}` }}>
                  <th
                    className="text-left py-3 text-sm font-medium"
                    style={{ color: chartColors.textSecondary }}
                  >
                    Title
                  </th>
                  <th
                    className="text-center py-3 text-sm font-medium"
                    style={{ color: chartColors.textSecondary }}
                  >
                    Category
                  </th>
                  <th
                    className="text-center py-3 text-sm font-medium"
                    style={{ color: chartColors.textSecondary }}
                  >
                    Views
                  </th>
                  <th
                    className="text-center py-3 text-sm font-medium"
                    style={{ color: chartColors.textSecondary }}
                  >
                    Likes
                  </th>
                  <th
                    className="text-center py-3 text-sm font-medium"
                    style={{ color: chartColors.textSecondary }}
                  >
                    Reading Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {performance.topPerforming.map((blog, index) => (
                  <tr
                    key={blog.id}
                    style={{ borderBottom: `1px solid ${chartColors.grid}` }}
                  >
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-medium"
                          style={{ color: chartColors.text }}
                        >
                          {index + 1}.
                        </span>
                        <span
                          className="text-sm"
                          style={{ color: chartColors.text }}
                        >
                          {blog.title}
                        </span>
                      </div>
                    </td>
                    <td className="text-center">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: chartColors.grid,
                          color: chartColors.textSecondary,
                        }}
                      >
                        {blog.category}
                      </span>
                    </td>
                    <td
                      className="text-center text-sm"
                      style={{ color: chartColors.info }}
                    >
                      {blog.views.toLocaleString()}
                    </td>
                    <td
                      className="text-center text-sm"
                      style={{ color: chartColors.secondary }}
                    >
                      {blog.likes.toLocaleString()}
                    </td>
                    <td
                      className="text-center text-sm"
                      style={{ color: chartColors.textSecondary }}
                    >
                      {blog.readingTime} min
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Commenters */}
      {engagement.topCommenters?.length > 0 && !blogId && (
        <div
          className="p-5 rounded-2xl border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          <h3
            className="font-semibold flex items-center gap-2 mb-4"
            style={{ color: chartColors.text }}
          >
            <FiUsers
              className="w-4 h-4"
              style={{ color: chartColors.success }}
            />
            Most Active Commenters
          </h3>
          <div className="space-y-3">
            {engagement.topCommenters.map((commenter, index) => (
              <div
                key={commenter.email}
                className="flex items-center gap-3 p-3 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/5"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                  {commenter.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <p
                    className="text-sm font-medium"
                    style={{ color: chartColors.text }}
                  >
                    {commenter.name}
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: chartColors.textSecondary }}
                  >
                    {commenter.email}
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className="text-lg font-bold"
                    style={{ color: chartColors.warning }}
                  >
                    {commenter.count}
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: chartColors.textSecondary }}
                  >
                    comments
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAnalyticsDashboard;
